import React, { useState, useEffect } from 'react';
import { useCreateOdp, useGetOdpFields } from '../../../apis';
import Button from '../../../../../components/ComponentsV1/Button';
import FieldRow from './FieldRow';
import { Skeleton } from '../../../../../components/ComponentsV1/Skeleton/Skeleton';
import { PROMO_TYPE, USER_ID_TYPE_OPTIONS } from '../constants';


const FORM_SKELETON_ROWS = [
  { cols: 3, height: '80px' },
  { cols: 2, height: '80px' },
  { cols: 1, height: '60px' },
];

function PromoForm({ promoType }) {
  const isUserLevel = promoType === PROMO_TYPE.USER_LEVEL;
  const [odpFields, setOdpFields] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [mandatoryFields, setMandatoryFields] = useState([]);
  const [errors, setErrors] = useState({});
  const [userIdType, setUserIdType] = useState(null);
  const [userIdValue, setUserIdValue] = useState('');

  useEffect(() => {
    setOdpFields([]);
    setInputValues({});
    setMandatoryFields([]);
    setErrors({});
    setUserIdType(null);
    setUserIdValue('');
  }, [promoType]);

  const { isLoading } = useGetOdpFields(promoType, {
    onSuccess: (response) => {
      if (!response?.inputs) return;

      const mandatoryInputFields = response.inputs.filter((item) => {
        return item?.optional === false;
      });

      const defaultInputValues = Object.fromEntries(
        response.inputs.map((input) => [input.key, null]),
      );

      setOdpFields(response.inputs || []);
      setInputValues(defaultInputValues);
      setMandatoryFields(mandatoryInputFields);
    },
  });

  const { mutate: createOdp, isLoading: createOdpLoading } = useCreateOdp({
    onSuccess: () => {
      document.getElementById('promosForm')?.reset();
      setInputValues({});
      setUserIdType(null);
      setUserIdValue('');
    },
  });

  const inputChangeHandler = (key, val, fieldType) => {
    const isStringField =
      fieldType === 'text_area' || fieldType === 'dropdown' || key === 'reason';
    const updatedInputs = {
      ...inputValues,
      [key]: isStringField ? val : Number(val),
    };
    setInputValues(updatedInputs);
  };

  const handleFieldFocus = (title) => {
    setErrors((prevState) => ({
      ...prevState,
      [title]: '',
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let checkfailed = false;
    setErrors({});

    if (isUserLevel) {
      if (!userIdType) {
        setErrors((prev) => ({ ...prev, user_id_type: 'ID type is required' }));
        checkfailed = true;
      }
      if (!userIdValue?.trim()) {
        setErrors((prev) => ({
          ...prev,
          user_id_value: 'ID value is required',
        }));
        checkfailed = true;
      }
    }

    mandatoryFields.every((obj) => {
      if (!inputValues?.[obj.key]) {
        setErrors((prevState) => ({
          ...prevState,
          [obj.title]: `${obj.title} is required`,
        }));
        checkfailed = true;
        return false;
      }
      return true;
    });

    if (!checkfailed) {
      const payload = { ...inputValues, promo_type: promoType };
      if (isUserLevel) {
        payload[userIdType] = userIdValue.trim();
      }
      createOdp(payload);
    }
  };

  if (isLoading || odpFields?.length === 0)
    return <Skeleton rows={FORM_SKELETON_ROWS} />;

  return (
    <form
      className='tw-w-full tw-flex tw-flex-col tw-gap-10 tw-items-end'
      id='promosForm'
    >
      <div className='tw-flex tw-w-full tw-gap-5'>
        <FieldRow
          fields={odpFields}
          startIndex={0}
          endIndex={isUserLevel ? 1 : 2}
          fieldWidth='tw-w-1/3'
          inputValues={inputValues}
          errors={errors}
          onInputChange={inputChangeHandler}
          onFieldFocus={handleFieldFocus}
        />
        {isUserLevel && (
          <div className='tw-w-1/3'>
            <label className='tw-w-full tw-flex tw-flex-col'>
              <div className='tw-w-full tw-mb-2 tw-flex tw-items-center tw-gap-1'>
                <select
                  value={userIdType || ''}
                  onChange={(e) => setUserIdType(e.target.value || null)}
                  className={`tw-text-zinc-800 tw-font-medium tw-text-[1.4rem] tw-bg-transparent tw-border-0 tw-outline-0 tw-cursor-pointer tw-p-0 tw-appearance-auto ${
                    errors.user_id_type ? 'tw-text-red-700' : ''
                  }`}
                >
                  <option value=''>Select ID Type</option>
                  {Object.entries(USER_ID_TYPE_OPTIONS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <input
                required
                value={userIdValue}
                onChange={(e) => setUserIdValue(e.target.value)}
                onFocus={() =>
                  setErrors((prev) => ({
                    ...prev,
                    user_id_type: '',
                    user_id_value: '',
                  }))
                }
                placeholder='Enter ID value'
                className={`tw-appearance-none tw-px-5 tw-h-20 tw-rounded-lg tw-outline-0 focus:tw-border-none focus:tw-ring-2 ${
                  errors.user_id_value || errors.user_id_type
                    ? 'tw-ring-red-700 tw-border-red-700'
                    : 'focus:tw-ring-base-green focus:tw-border-base-green tw-border-grey-300'
                } tw-border tw-border-solid`}
              />
              {(errors.user_id_type || errors.user_id_value) && (
                <p className='tw-mt-2 tw-text-red-700'>
                  {errors.user_id_type || errors.user_id_value}
                </p>
              )}
            </label>
          </div>
        )}
      </div>
      <div className='tw-flex tw-w-full tw-gap-5'>
        <FieldRow
          fields={odpFields}
          startIndex={isUserLevel ? 2 : 3}
          endIndex={4}
          fieldWidth='tw-w-1/2'
          inputValues={inputValues}
          errors={errors}
          onInputChange={inputChangeHandler}
          onFieldFocus={handleFieldFocus}
        />
      </div>

      <Button
        onClick={handleSubmit}
        size='large'
        title={'Create Promo'}
        loading={createOdpLoading}
      />
    </form>
  );
}

export default PromoForm;
